import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../store';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? ((window.location.hostname === 'devapp.soviewfx.fr' || window.location.hostname === 'devadmin.soviewfx.fr') ? 'https://devhub.soviewfx.fr' : ((window.location.hostname === 'app.soviewfx.fr' || window.location.hostname === 'admin.soviewfx.fr') ? 'https://hub.soviewfx.fr' : '')) : '');

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const { user, token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/home'); return; }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [plansRes, subRes] = await Promise.all([
        fetch(`${API_URL}/api/plans`, { headers }),
        fetch(`${API_URL}/api/subscription`, { headers })
      ]);
      if (plansRes.ok) {
        const data = await plansRes.json();
        setPlans(data.plans || []);
      }
      if (subRes.ok) {
        const subData = await subRes.json();
        setSubscription(subData);
      }
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleChoosePlan = async (planId) => {
    setActionLoading(planId);
    try {
      const res = await fetch(`${API_URL}/api/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          price_id: planId,
          success_url: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/subscription`
        })
      });
      if (!res.ok) throw new Error('Checkout failed');
      const { url } = await res.json();
      if (url) window.location.href = url;
    } catch (err) {
      toast.error('Erreur lors de la création du paiement');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir résilier votre abonnement ?')) return;
    setActionLoading('cancel');
    try {
      const res = await fetch(`${API_URL}/api/subscription/cancel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!res.ok) throw new Error('Cancel failed');
      toast.success('Abonnement résilié');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la résiliation');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric'
    });
  };

  const formatPrice = (cents, currency) => {
    const amount = (cents / 100).toFixed(2);
    const symbol = currency === 'eur' ? '€' : '$';
    return `${amount} ${symbol}`;
  };

  if (loading) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <header className="h-14 glass flex items-center px-4 gap-4 flex-shrink-0">
        <button onClick={() => navigate('/home')} className="p-2 rounded-lg hover:bg-white/5">
          <ArrowLeft size={20} />
        </button>
        <h1 className="font-display font-bold text-lg uppercase tracking-wide">
          Abonnement
        </h1>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6 pb-safe">
        {/* Current Subscription */}
        {subscription && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="card p-5"
          >
            <h2 className="font-display font-bold text-primary uppercase tracking-wide mb-3">
              Abonnement actuel
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Plan</span>
                <span className="font-semibold text-white">{subscription.plan?.name || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Statut</span>
                <span className={`font-semibold ${subscription.status === 'active' ? 'text-green-400' : subscription.status === 'canceled' ? 'text-yellow-400' : 'text-muted-foreground'}`}>
                  {subscription.status === 'active' ? 'Actif' : subscription.status === 'canceled' ? 'Résilié' : subscription.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Fin de période</span>
                <span className="font-semibold text-white">{formatDate(subscription.current_period_end)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Crédits restants</span>
                <span className="font-semibold text-primary">{subscription.credits_remaining ?? '—'}</span>
              </div>
            </div>
            {subscription.status === 'active' && (
              <button
                onClick={handleCancel}
                disabled={actionLoading === 'cancel'}
                className="btn-ghost mt-4 w-full text-red-400 hover:text-red-300 border border-red-400/30 hover:bg-red-500/10"
              >
                {actionLoading === 'cancel' ? (
                  <Loader2 size={16} className="animate-spin mx-auto" />
                ) : 'Résilier l\'abonnement'}
              </button>
            )}
          </motion.div>
        )}

        {/* Available Plans */}
        <div>
          <h2 className="font-display font-bold text-lg uppercase tracking-wide text-white mb-4">
            Nos offres
          </h2>
          {plans.length === 0 ? (
            <p className="text-muted-foreground text-sm">Aucune offre disponible pour le moment.</p>
          ) : (
            <div className="grid gap-4">
              {plans.map((plan, i) => (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className={`card p-5 ${subscription?.plan?.id === plan.id ? 'border-primary/50 shadow-neon-gold' : ''}`}
                >
                  {/* Plan Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-display font-bold text-lg text-white">{plan.name}</h3>
                      <p className="text-2xl font-bold text-primary mt-1">
                        {formatPrice(plan.price_cents, plan.currency)}
                        <span className="text-sm text-muted-foreground font-normal">
                          /{plan.interval === 'month' ? 'mois' : 'an'}
                        </span>
                      </p>
                    </div>
                    {plan.credits_per_month && (
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Crédits/mois</p>
                        <p className="font-bold text-secondary">{plan.credits_per_month}</p>
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  {plan.features && plan.features.length > 0 && (
                    <ul className="space-y-1.5 mb-4">
                      {plan.features.map((feat, fi) => (
                        <li key={fi} className="flex items-center gap-2 text-sm">
                          {feat.startsWith('✗') || feat.startsWith('✕') || feat.startsWith('—') ? (
                            <X size={14} className="text-red-400 flex-shrink-0" />
                          ) : (
                            <Check size={14} className="text-green-400 flex-shrink-0" />
                          )}
                          <span className="text-muted-foreground">{feat.replace(/^[✗✕—]\s*/, '')}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Action */}
                  <button
                    onClick={() => handleChoosePlan(plan.id)}
                    disabled={actionLoading === plan.id || subscription?.plan?.id === plan.id}
                    className={`w-full py-2.5 rounded-full font-bold uppercase tracking-wider text-sm transition-all active:scale-95 ${
                      subscription?.plan?.id === plan.id
                        ? 'bg-secondary/20 text-secondary cursor-not-allowed'
                        : 'btn-primary'
                    }`}
                  >
                    {actionLoading === plan.id ? (
                      <Loader2 size={18} className="animate-spin mx-auto" />
                    ) : subscription?.plan?.id === plan.id ? (
                      'Plan actuel'
                    ) : (
                      'Choisir'
                    )}
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
